import { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { fetchWithAuth } from '../libs/apiUtils'
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import { TrashIcon, CloudArrowUpIcon, SquaresPlusIcon, TableCellsIcon, KeyIcon, ArrowDownTrayIcon, DocumentArrowUpIcon, EyeIcon, EyeSlashIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

interface Row {
  rowId: string | null
  rowName: string | null
  rowShownAtIndex: number | null
  values: Column[]
}

interface Column {
  columnId: string | null
  columnName: string | null
  columnShownAtIndex: number | null
  value: string
  dataType?: 'int' | 'float' | 'string'
}

export function TableContent() {
  const { tableId } = useParams<{ tableId: string }>()

  const [rows, setRows] = useState<Row[]>([])
  const [columns, setColumns] = useState<Column[]>([])

  const [tableName, setTableName] = useState<string>('')
  const [groupName, setGroupName] = useState<string>('')
  const [categoryName, setCategoryName] = useState<string>('')
  const [keyColumnId, setKeyColumnId] = useState<string | null>(null)

  const [loading, setLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const [showDeleteIcons, setShowDeleteIcons] = useState(false)
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>([])
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false)
  const visibilityMenuRef = useRef<HTMLDivElement>(null)
  
  const fileInputRef = useRef<HTMLInputElement>(null)

  const userRoleSlug = localStorage.getItem('user_role_slug')
  const adminRoles = ['admin', 'super_admin']
  const isAdmin = adminRoles.includes(userRoleSlug ?? '')
  const isUser = userRoleSlug === 'user'
  const doesUserHaveAccess = isAdmin || isUser

  const navigate = useNavigate()

  const loadTableContent = useCallback(async () => {
    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-content/read`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Internal-Request': 'true'
        },
        body: JSON.stringify({ tableId }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        toast.error(data.message || 'Gagal memuat konten tabel')
        return
      }

      const rawRows: Row[] = data.data?.rows || []
      const rawCols: any[] = data.data?.columns || []

      const columnMap: Record<string, Column> = {}
      
      rawCols.forEach((col: any) => {
        const colKey = col.columnId || col.columnName
        columnMap[colKey] = {
          columnId: col.columnId,
          columnName: col.columnName,
          columnShownAtIndex: col.columnShownAtIndex,
          value: '',
          dataType: col.dataType || 'string'
        }
      })

      rawRows.forEach((row) => {
        row.values?.forEach((val) => {
          const colKey = val.columnId || val.columnName
          if (colKey && !columnMap[colKey]) {
            columnMap[colKey] = {
              columnId: val.columnId ?? null,
              columnName: val.columnName || `KOLOM ${Object.keys(columnMap).length + 1}`,
              columnShownAtIndex: val.columnShownAtIndex ?? Object.keys(columnMap).length,
              value: val.value,
              dataType: 'string'
            }
          }
        })
      })

      const sortedColumns = Object.values(columnMap).sort((a, b) => 
        (a.columnShownAtIndex ?? 0) - (b.columnShownAtIndex ?? 0)
      )

      const sortedRows = Object.values(rawRows).sort((a, b) => 
        (a.rowShownAtIndex ?? 0) - (b.rowShownAtIndex ?? 0)
      )
      
      setColumns(sortedColumns)
      setRows(sortedRows)
      
      // Default all columns visible
      const allColKeys = sortedColumns.map(c => c.columnId || String(c.columnShownAtIndex))
      setVisibleColumnIds(allColKeys)
      
      setHasChanges(false)
    } catch (error) {
      toast.error('Kesalahan jaringan saat memuat konten')
    } finally {
      setLoading(false)
    }
  }, [tableId])

  function toggleColumnVisibility(colKey: string) {
    setVisibleColumnIds(prev => 
      prev.includes(colKey) 
        ? prev.filter(id => id !== colKey) 
        : [...prev, colKey]
    )
  }

  function handleCellChange(rowIdx: number, colIdx: number, value: string) {
    const col = columns[colIdx]
    const isKeyColumn = col.columnId === keyColumnId
    
    if (!isKeyColumn) {
      if (col.dataType === 'int' && value !== '' && !/^-?\d+$/.test(value)) return
      if (col.dataType === 'float' && value !== '' && !/^-?\d*\.?\d*$/.test(value)) return
    }

    setRows(prevRows => {
      return prevRows.map((row, rIdx) => {
        if (rIdx !== rowIdx) return row

        const newValues = row.values.map(cell =>
          (cell.columnShownAtIndex === colIdx)
            ? { ...cell, value }
            : cell
        )

        const hasCell = newValues.some(c => c.columnShownAtIndex === colIdx)
        return {
          ...row,
          values: hasCell
            ? newValues
            : [...newValues, { columnId: null, columnName: null, columnShownAtIndex: colIdx, value }]
        }
      })
    })
    setHasChanges(true)
  }

  function handleColumnNameChange(colIdx: number, value: string) {
    const upperValue = value.toUpperCase()
    setColumns(prevCols => {
      const updatedCols = [...prevCols]
      updatedCols[colIdx].columnName = upperValue
      return updatedCols
    })
    setRows(prevRows => {
      return prevRows.map(row => ({
        ...row,
        values: row.values.map(cell => {
          if ((cell.columnId && cell.columnId === columns[colIdx].columnId) || 
            (cell.columnShownAtIndex === columns[colIdx].columnShownAtIndex)) {
            return { ...cell, columnName: upperValue }
          }
          return cell 
        })
      }))
    })
    setHasChanges(true)
  }

  function handleColumnDataTypeChange(colIdx: number, value: 'int' | 'float' | 'string') {
    setColumns(prevCols => {
      const updatedCols = [...prevCols]
      updatedCols[colIdx].dataType = value
      return updatedCols
    })
    setHasChanges(true)
  }

  function addNewRow() {
    const newRow: Row = {
      rowId: null,
      rowName: null,
      rowShownAtIndex: rows.length,
      values: columns.map(col => ({
        columnId: col.columnId,
        columnName: col.columnName,
        columnShownAtIndex: col.columnShownAtIndex,
        value: '',
      }))
    }
    setRows(prev => [...prev, newRow])
    setHasChanges(true)
  }

  function addNewColumn() {
    const newName = `KOLOM ${columns.length + 1}`
    const newColumn: Column = {
      columnId: null,
      columnName: newName,
      columnShownAtIndex: columns.length,
      value: '',
      dataType: 'string',
    }
    setColumns(prev => [...prev, newColumn])
    // Add to visible columns by default
    setVisibleColumnIds(prev => [...prev, String(columns.length)])
    
    setRows(prevRows => {
      if (prevRows.length === 0) {
        return [{
          rowId: null,
          rowName: null,
          rowShownAtIndex: 0,
          values: [{ ...newColumn }]
        }]
      }
      return prevRows.map(row => ({
        ...row,
        values: [...row.values, { ...newColumn }]
      }))
    })
    setHasChanges(true)
  }

  const saveChanges = useCallback(async () => {
    try {
      setLoading(true)
      const payload = {
        tableId,
        keyColumnId,
        rows: rows.map(row => ({
          rowId: row.rowId,
          rowShownAtIndex: row.rowShownAtIndex,
          values: row.values.map(value => {
            const col = columns.find(c => (c.columnId && c.columnId === value.columnId) || c.columnShownAtIndex === value.columnShownAtIndex)
            return {
              columnId: value.columnId,
              columnName: value.columnName,
              columnShownAtIndex: value.columnShownAtIndex,
              value: String(value.value ?? ''),
              dataType: col?.dataType || 'string'
            }
          })
        }))
      }
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-content/create`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Internal-Request': 'true'
        },
        body: JSON.stringify(payload)
      })
      const data = await res.json()
      if (!res.ok || !data.success) throw new Error(data.message || 'Gagal menyimpan perubahan')
      toast.success('Perubahan berhasil disimpan')
      setHasChanges(false)
      await loadTableContent()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal menyimpan perubahan')
    } finally {
      setLoading(false)
    }
  }, [tableId, keyColumnId, rows, columns, loadTableContent])

  async function deleteColumn(columnIdx: number) {
    const column = columns[columnIdx]
    if (column.columnId) {
      try {
        setLoading(true)
        const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/column/${column.columnId}`, { method: 'DELETE' })
        const data = await res.json()
        if (!res.ok || !data.success) throw new Error(data.message || 'Gagal menghapus kolom')
        toast.success('Kolom berhasil dihapus')
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Gagal menghapus kolom')
        return
      } finally {
        setLoading(false)
      }
    }
    const colKey = column.columnId || String(column.columnShownAtIndex)
    setVisibleColumnIds(prev => prev.filter(id => id !== colKey))
    setColumns(prev => prev.filter((_, idx) => idx !== columnIdx))
    setRows(prevRows => prevRows.map(row => ({
        ...row,
        values: row.values.filter(val => val.columnId !== column.columnId && val.columnShownAtIndex !== column.columnShownAtIndex)
    })))
  }

  async function deleteRow(rowIdx: number) {
    const row = rows[rowIdx]
    if (row.rowId) {
      try {
        setLoading(true)
        const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/row/${row.rowId}`, { method: 'DELETE' })
        const data = await res.json()
        if (!res.ok || !data.success) throw new Error(data.message || 'Gagal menghapus baris')
        toast.success('Baris berhasil dihapus')
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Gagal menghapus baris')
        return
      } finally {
        setLoading(false)
      }
    }
    setRows(prev => prev.filter((_, idx) => idx !== rowIdx))
  }

  async function handleDownloadXLSX() {
    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-content/template/${tableId}`, {
        method: 'GET',
      })

      if (!res.ok) throw new Error('Gagal mengunduh template')

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${tableName}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      toast.success('Template berhasil diunduh')
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal mengunduh template')
    } finally {
      setLoading(false)
    }
  }

  async function handleUploadXLSX(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('tableId', tableId!)

    try {
      setLoading(true)
      const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-content/upload`, {
        method: 'POST',
        body: formData,
      })

      const data = await res.json()
      if (!res.ok || !data.success) throw new Error(data.message || 'Gagal mengunggah XLSX')

      toast.success('XLSX berhasil diunggah')
      await loadTableContent()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal mengunggah XLSX')
    } finally {
      setLoading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  useEffect(() => {
    async function validateTable() {
      try {
        setLoading(true)
        const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/api/table-detail`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tableId }),
        })
        const data = await res.json()
        if (!res.ok || !data.success) throw new Error(data.message || 'Tabel tidak valid')
        setTableName(data.data.name || '')
        setCategoryName(data.data.category?.name || '')
        setGroupName(data.data.category?.group?.name || '')
        setKeyColumnId(data.data.keyColumnId || null)
      } catch (err) {
        toast.error('Tabel tidak ditemukan')
        navigate('/data/group')
      } finally {
        setLoading(false)
      }
    }
    if (tableId) validateTable()
  }, [tableId, navigate])

  useEffect(() => {
    if (tableId) loadTableContent()
  }, [tableId, loadTableContent])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (visibilityMenuRef.current && !visibilityMenuRef.current.contains(event.target as Node)) {
        setShowVisibilityMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        if (hasChanges && !loading) {
          e.preventDefault()
          saveChanges()
        } else if (e.key === 's') {
          // If no changes, still prevent default to avoid browser save dialog
          e.preventDefault()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hasChanges, loading, saveChanges])



  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-6 rounded-2xl shadow-xl shadow-neutral-100 border border-neutral-100">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-neutral-100 rounded-full transition-colors text-neutral-600">
            <ArrowLeftIcon className="size-6" />
          </button>
          <div>
            <nav className="flex text-sm text-neutral-500 mb-1">
              <button onClick={() => navigate('/data/group')} className="hover:text-primary-600 transition-colors">Manajemen Grup</button>
              <span className="mx-2">/</span>
              <button 
                onClick={() => navigate(-3)} 
                className="hover:text-primary-600 transition-colors"
              >
                {groupName || '...'}
              </button>
              <span className="mx-2">/</span>
              <button 
                onClick={() => navigate(-1)} 
                className="hover:text-primary-600 transition-colors"
              >
                {categoryName || '...'}
              </button>
              <span className="mx-2">/</span>
              <span className="text-neutral-900 font-medium">{tableName || '...'}</span>
            </nav>
            <h2 className="text-2xl font-extrabold text-neutral-900 flex items-center gap-3">
              {tableName || '...'}
            </h2>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          {isAdmin ? (
            <div className="flex items-center gap-2 bg-neutral-50 px-4 py-2 rounded-xl border border-neutral-200">
              <KeyIcon className="size-5 text-yellow-600" />
              <label htmlFor="keyColumnSelect" className="text-sm font-bold text-neutral-700 whitespace-nowrap">Kolom Kunci:</label>
              <select
                id="keyColumnSelect"
                className="bg-transparent text-sm font-semibold text-neutral-900 focus:outline-none"
                value={keyColumnId || ''}
                onChange={(e) => {
                  setKeyColumnId(e.target.value || null)
                  setHasChanges(true)
                }}
              >
                <option value="">-- Pilih Kolom Kunci --</option>
                {columns.map(col => (
                  <option key={col.columnId || col.columnShownAtIndex} value={col.columnId || ''}>
                    {col.columnName}
                  </option>
                ))}
              </select>
            </div>
          ) : keyColumnId && (
            <div className="flex items-center gap-2 bg-yellow-50 px-4 py-2 rounded-xl border border-yellow-100">
              <KeyIcon className="size-5 text-yellow-600" />
              <span className="text-sm font-bold text-neutral-700">Kolom Kunci:</span>
              <span className="text-sm font-semibold text-neutral-900">
                {columns.find(c => c.columnId === keyColumnId)?.columnName || 'Tidak ada'}
              </span>
            </div>
          )}

          <button
            onClick={saveChanges}
            disabled={!hasChanges || loading}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold shadow-lg transition-all active:scale-95 ${
                hasChanges ? 'bg-green-600 hover:bg-green-700 text-white shadow-green-200' : 'bg-neutral-100 text-neutral-400 cursor-not-allowed shadow-none'
            }`}
          >
            <CloudArrowUpIcon className="size-5" />
            {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl shadow-neutral-100 border border-neutral-100 relative">
        <div className="p-4 bg-neutral-50/50 border-b border-neutral-100 flex flex-wrap justify-between items-center gap-4">
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <button 
                onClick={addNewRow}
                disabled={columns.length === 0}
                className="flex items-center gap-2 bg-white hover:bg-neutral-50 text-neutral-700 px-4 py-2 rounded-lg text-sm font-bold border border-neutral-200 transition-all active:scale-95 disabled:opacity-50"
              >
                <TableCellsIcon className="size-4 text-primary-600" />
                Tambah Baris
              </button>
            )}
            {doesUserHaveAccess && (
              <button 
                onClick={addNewColumn}
                className="flex items-center gap-2 bg-white hover:bg-neutral-50 text-neutral-700 px-4 py-2 rounded-lg text-sm font-bold border border-neutral-200 transition-all active:scale-95"
              >
                <SquaresPlusIcon className="size-4 text-yellow-600" />
                Tambah Kolom
              </button>
            )}
            <div className="w-px h-8 bg-neutral-200 mx-2 hidden sm:block"></div>
            
            <div className="relative" ref={visibilityMenuRef}>
              <button 
                onClick={() => setShowVisibilityMenu(!showVisibilityMenu)}
                className="flex items-center gap-2 bg-white hover:bg-neutral-50 text-neutral-700 px-4 py-2 rounded-lg text-sm font-bold border border-neutral-200 transition-all active:scale-95"
              >
                <EyeIcon className="size-4 text-neutral-500" />
                Visibilitas Kolom
                <ChevronDownIcon className={`size-3 transition-transform ${showVisibilityMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showVisibilityMenu && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-neutral-100 z-50 p-2 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="p-2 border-b border-neutral-50 mb-2 flex items-center justify-between">
                    <span className="text-xs font-black text-neutral-400 uppercase tracking-widest">Pilih Kolom</span>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => setVisibleColumnIds(columns.map(c => c.columnId || String(c.columnShownAtIndex)))}
                        className="text-[10px] font-bold text-primary-600 hover:underline"
                      >
                        Semua
                      </button>
                      <button 
                        onClick={() => setVisibleColumnIds([])}
                        className="text-[10px] font-bold text-red-600 hover:underline"
                      >
                        Sembunyi
                      </button>
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto space-y-1 scrollbar-thin scrollbar-thumb-neutral-200">
                    {columns.map(col => {
                      const colKey = col.columnId || String(col.columnShownAtIndex)
                      const isVisible = visibleColumnIds.includes(colKey)
                      return (
                        <button
                          key={colKey}
                          onClick={() => toggleColumnVisibility(colKey)}
                          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isVisible ? 'bg-primary-50 text-primary-700' : 'text-neutral-500 hover:bg-neutral-50'
                          }`}
                        >
                          {isVisible ? <EyeIcon className="size-4" /> : <EyeSlashIcon className="size-4 opacity-50" />}
                          <span className="truncate uppercase">{col.columnName}</span>
                          {col.columnId === keyColumnId && (
                            <KeyIcon className="size-3 text-yellow-600 ml-auto flex-shrink-0" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            <button 
              onClick={handleDownloadXLSX}
              className="flex items-center gap-2 bg-white hover:bg-primary-50 text-primary-600 px-4 py-2 rounded-lg text-sm font-bold border border-primary-100 transition-all active:scale-95"
            >
              <ArrowDownTrayIcon className="size-4" />
              Unduh XLSX
            </button>
            <button 
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg shadow-primary-100 transition-all active:scale-95"
            >
              <DocumentArrowUpIcon className="size-4" />
              Unggah XLSX
            </button>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleUploadXLSX} 
              accept=".xlsx" 
              className="hidden" 
            />
          </div>
          {doesUserHaveAccess && (
            <button 
              onClick={() => setShowDeleteIcons(!showDeleteIcons)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                showDeleteIcons ? 'bg-red-50 text-red-600 border border-red-100 shadow-md' : 'bg-white text-neutral-600 border border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              <TrashIcon className="size-4" />
              {showDeleteIcons ? 'Selesai Menghapus' : 'Mode Hapus Konten'}
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-max">
            <thead>
              <tr className="bg-neutral-50/30">
                {(showDeleteIcons && isAdmin) && <th className="px-4 py-4 w-12 border-b"></th>}
                <th className="px-4 py-4 border-b border-neutral-100 bg-neutral-50/50 w-16 text-center">
                  <span className="text-xs font-black text-neutral-400 uppercase tracking-widest">#</span>
                </th>
                {columns.map((col, colIdx) => {
                  const colKey = col.columnId || String(col.columnShownAtIndex)
                  if (!visibleColumnIds.includes(colKey)) return null
                  
                  return (
                    <th key={col.columnId || colIdx} className={`px-4 py-4 border-b border-neutral-100 transition-all ${showDeleteIcons ? 'bg-red-50/30' : ''}`}>
                      <div className={`flex items-center gap-2 p-2 rounded-lg transition-all ${showDeleteIcons ? 'bg-white shadow-sm border border-red-100' : ''}`}>
                        <div className="flex flex-col gap-1 w-full">
                          <input
                            type="text"
                            value={col.columnName ?? ''}
                            onChange={(e) => handleColumnNameChange(colIdx, e.target.value)}
                            className={`bg-transparent text-sm font-extrabold text-neutral-700 uppercase tracking-wider focus:outline-none focus:text-primary-600 transition-colors ${(!doesUserHaveAccess || (!isAdmin && col.columnId === keyColumnId)) ? 'cursor-default' : ''}`}
                            disabled={!doesUserHaveAccess || (!isAdmin && col.columnId === keyColumnId)}
                            placeholder="NAMA KOLOM"
                          />
                          <select
                            value={col.dataType || 'string'}
                            onChange={(e) => handleColumnDataTypeChange(colIdx, e.target.value as any)}
                            disabled={!doesUserHaveAccess || (!isAdmin && col.columnId === keyColumnId)}
                            className={`text-xs font-semibold bg-neutral-100 text-neutral-600 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary-300 w-fit ${(!doesUserHaveAccess || (!isAdmin && col.columnId === keyColumnId)) ? 'cursor-default opacity-70' : 'cursor-pointer'}`}
                          >
                            <option value="string">String</option>
                            <option value="int">Bulat</option>
                            <option value="float">Desimal</option>
                          </select>
                        </div>
                        {showDeleteIcons && (
                          <button onClick={() => deleteColumn(colIdx)} className="text-red-400 hover:text-red-600 p-1 hover:bg-red-50 rounded transition-colors">
                            <TrashIcon className="size-4" />
                          </button>
                        )}
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1 + (showDeleteIcons && isAdmin ? 1 : 0)} className="px-6 py-20 text-center">
                    <div className="bg-neutral-50 size-16 rounded-full flex items-center justify-center mx-auto mb-4 text-neutral-400">
                      <TableCellsIcon className="size-8" />
                    </div>
                    <p className="text-neutral-500 font-medium italic">Belum ada konten untuk ditampilkan</p>
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIdx) => (
                  <tr key={row.rowId || rowIdx} className={`group transition-colors ${showDeleteIcons ? 'bg-red-50/10 hover:bg-red-50/20' : 'hover:bg-primary-50/20'}`}>
                    {(showDeleteIcons && isAdmin) && (
                      <td className="px-4 py-3 border-r border-neutral-50">
                        <div className="bg-white shadow-sm border border-red-100 rounded-lg p-1.5 flex items-center justify-center">
                          <button onClick={() => deleteRow(rowIdx)} className="text-red-400 hover:text-red-600 transition-colors">
                            <TrashIcon className="size-5" />
                          </button>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-3 border-r border-neutral-50 bg-neutral-50/20 text-center text-xs font-bold text-neutral-400">
                      {rowIdx + 1}
                    </td>
                    {columns.map((col, colIdx) => {
                      const colKey = col.columnId || String(col.columnShownAtIndex)
                      if (!visibleColumnIds.includes(colKey)) return null

                      const cell = row.values.find(v => v.columnId ? v.columnId === col.columnId : v.columnShownAtIndex === col.columnShownAtIndex)
                      const isKey = col.columnId === keyColumnId
                      return (
                        <td key={`row-${rowIdx}-col-${colIdx}`} className={`px-4 py-3 border-r border-neutral-50 last:border-r-0 ${isKey ? 'bg-yellow-50/30' : ''} ${showDeleteIcons ? 'bg-red-50/5' : ''}`}>
                          <div className={`transition-all ${showDeleteIcons ? 'bg-white/50 shadow-sm border border-red-50 rounded px-1' : ''}`}>
                            <input
                              type="text"
                              value={cell?.value || ''}
                              onChange={(e) => handleCellChange(rowIdx, colIdx, e.target.value)}
                              className={`w-full bg-transparent text-sm text-neutral-800 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-200 rounded px-2 py-1 transition-all ${isKey ? 'font-bold text-neutral-900' : ''} ${!isAdmin && isKey ? 'cursor-not-allowed opacity-70' : ''}`}
                              disabled={!isAdmin && isKey}
                              placeholder="..."
                            />
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {hasChanges && (
        <div className="fixed bottom-8 left-64 right-0 z-40 flex justify-center pointer-events-none">
          <div className="bg-neutral-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-4 animate-bounce pointer-events-auto">
              <div className="size-2 bg-yellow-400 rounded-full"></div>
              <span className="text-sm font-bold flex items-center gap-2">
                Ada perubahan yang belum disimpan
                <kbd className="bg-neutral-800 px-1.5 py-0.5 rounded text-[10px] border border-neutral-700">CTRL + S</kbd>
              </span>
              <button onClick={saveChanges} className="bg-white text-neutral-900 px-4 py-1.5 rounded-lg text-xs font-black uppercase hover:bg-neutral-100 transition-colors">
                  Simpan Sekarang
              </button>
          </div>
        </div>
      )}
    </div>
  )
}
